import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'

export type ExportModule = 'resource' | 'candidate' | 'recruitment' | 'project' | 'timesheet' | 'evaluation' | 'leave' | 'training' | 'invoice' | 'expense'

export type DatePreset = 'today' | 'thisWeek' | 'thisMonth' | 'lastMonth' | 'thisYear' | 'yearToDate' | 'lastYear' | 'last3Months' | 'last6Months' | 'custom' | 'singleDate' | null

export type ExportFieldDefinition = {
  key: string
  label: string
  group: string
}

export type ExportTemplate = {
  _id: string
  _created: string
  _updated: string
  _firmId: string
  createdBy: { _id: string, firstname: string, lastname: string }
  name: string
  module: ExportModule
  fields: string[]
  datePreset: DatePreset
  dateField?: string | null
  filters: Record<string, any>
  isDefault: boolean
}

const resource = 'exportTemplates'

const list = (axios: IFactoryFunctionRequest<ExportTemplate[], any>) => (module?: ExportModule) => {
  const query = module ? { module } : {}
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}`,
    query,
  })
}

const getModuleFields = (axios: IFactoryFunctionRequest<ExportFieldDefinition[], any>) => (module: ExportModule) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/fields/${module}`,
  })
}

const create = (axios: IFactoryFunctionRequest<ExportTemplate, any>) => (data: {
  name: string
  module: ExportModule
  fields: string[]
  datePreset?: DatePreset
  dateField?: string | null
  filters?: Record<string, any>
}) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}`,
    data,
  })
}

const update = (axios: IFactoryFunctionRequest<ExportTemplate, any>) => (id: string, data: {
  name?: string
  fields?: string[]
  datePreset?: DatePreset
  dateField?: string | null
  filters?: Record<string, any>
}) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${id}`,
    data,
  })
}

const remove = (axios: IFactoryFunctionRequest<{ message: string }, any>) => (id: string) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${id}`,
  })
}

export default (axios: AxiosInstance) => ({
  list: list(useAxios(axios)),
  getModuleFields: getModuleFields(useAxios(axios)),
  create: create(useAxios(axios)),
  update: update(useAxios(axios)),
  remove: remove(useAxios(axios)),
})
